import "./App.css";
import logo from "./images/logo.png";
import MedicareAdvantageIntakeForm from "./leadCapture";

function App() {
  const benefits = [
    {
      title: "Confidence in Your Coverage",
      text: "Understand Medicare Advantage in clear, simple terms with support that helps you compare plans with confidence.",
      icon: "shield",
    },
    {
      title: "Guidance From Real People",
      text: "Work with a team that helps you review benefits, doctors, prescriptions, and everyday healthcare needs.",
      icon: "support",
    },
    {
      title: "Support Beyond Enrollment",
      text: "We stay with you after you enroll, helping answer questions and making the next steps easier to navigate.",
      icon: "handshake",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Schedule a Consultation",
      text: "Speak with our team about your healthcare needs, prescriptions, and current coverage.",
    },
    {
      number: "02",
      title: "Review Your Options",
      text: "We help you compare Medicare Advantage plans in a way that is easy to understand.",
    },
    {
      number: "03",
      title: "Enroll with Confidence",
      text: "Once you are ready, we guide you through the enrollment process step by step.",
    },
  ];

  const renderIcon = (icon) => {
    switch (icon) {
      case "shield":
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true" className="ma-icon-svg">
            <path
              d="M12 3l7 3v5c0 4.8-2.8 8.7-7 10-4.2-1.3-7-5.2-7-10V6l7-3z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M9 12l2 2 4-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "support":
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true" className="ma-icon-svg">
            <path
              d="M4 13v-1a8 8 0 1116 0v1"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M6 13h1a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 012-2zm11 0h1a2 2 0 012 2v2a2 2 0 01-2 2h-1a2 2 0 01-2-2v-2a2 2 0 012-2z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M12 19a3 3 0 003-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true" className="ma-icon-svg">
            <path
              d="M7 12l4 4 6-8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 12c2.5-3.5 5.5-5.2 9-5.2 3.4 0 6.3 1.7 9 5.2-2.7 3.5-5.6 5.2-9 5.2-3.5 0-6.5-1.7-9-5.2z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            />
          </svg>
        );
    }
  };

  return (
    <div className="ma-page">
      <header className="ma-header">
        <div className="ma-header-overlay" />
        <nav className="ma-nav">
          <div className="ma-brand-wrap">
            <img src={logo} alt="Meda Advantage" className="ma-logo" />
          </div>

          <div className="ma-nav-links">
            <a href="#about-us">About Us</a>
            <a href="#contact">Contact</a>
            <a href="#get-a-quote" className="ma-nav-cta">
              Get a Quote
            </a>
          </div>
        </nav>

        <section className="ma-hero">
          <div className="ma-hero-copy">
            <p className="ma-kicker">Client-Focused Medicare Guidance</p>
            <h1 className="ma-hero-title">
              Medicare Advantage
              <span>Made Simple</span>
            </h1>
            <p className="ma-hero-text">
              Finding the right Medicare Advantage plan does not have to be
              overwhelming. Meda Health helps you compare options, understand
              benefits, and move forward with confidence.
            </p>

            <div className="ma-actions">
              <a href="#get-a-quote" className="ma-button ma-button-primary">
                Get a Quote
              </a>
              <a href="#about-us" className="ma-button ma-button-secondary">
                About Us
              </a>
            </div>
          </div>
        </section>
      </header>

      <main>
        <section id="about-us" className="ma-section ma-section-light">
          <div className="ma-section-heading">
            <p className="ma-section-kicker">About Us</p>
            <h2>Why clients choose Meda Advantage</h2>
            <p>
              We help individuals and families understand Medicare Advantage
              with a clear, supportive, and personalized approach.
            </p>
          </div>

          <div className="ma-benefit-grid">
            {benefits.map((benefit) => (
              <article key={benefit.title} className="ma-benefit-card">
                <div className="ma-icon">{renderIcon(benefit.icon)}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ma-section ma-section-soft">
          <div className="ma-section-heading">
            <p className="ma-section-kicker">How It Works</p>
            <h2>A simple path to better Medicare guidance</h2>
            <p>
              From your first call to enrollment support, our team is here to
              help make the process easier.
            </p>
          </div>

          <div className="ma-steps-grid">
            {steps.map((step) => (
              <article key={step.number} className="ma-step-card">
                <div className="ma-step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="get-a-quote" className="ma-quote-band">
          <div className="ma-quote-content">
            <p className="ma-section-kicker ma-section-kicker-light">
              Get a Quote
            </p>
            <h2>Ready to explore your Medicare Advantage options?</h2>
            <p>
              Connect with Meda Health to review plan options, benefits,
              provider access, and enrollment support tailored to your needs.
            </p>
            <div className="ma-intake">
              <MedicareAdvantageIntakeForm></MedicareAdvantageIntakeForm>
            </div>
          </div>
        </section>
      </main>

      <footer id="contact" className="ma-footer">
        <img src={logo} alt="Meda Advantage" className="ma-footer-logo" />
        <div className="ma-footer-contact">
          <a href="mailto:info@medahealth.com">info@medahealth.com</a>
        </div>
        <div className="ma-footer-links">
          <a href="#about-us">About Us</a>
          <a href="#contact">Contact</a>
          <a href="#get-a-quote">Get a Quote</a>
        </div>
        <p className="ma-footer-note">Copyright MedaAdvantage 2026</p>
      </footer>
    </div>
  );
}

export default App;
